import type { ModelMessage } from "ai";
import { hasPendingApprovals } from "./tool-approvals";
import type { AgentChatMessage, AgentChatStatus } from "./types";

export type AgentChatMessageListScroll = {
	scrollTop: number;
	stickToBottom: boolean;
};

export type AgentChatSession = {
	chatId: string | null;
	messages: AgentChatMessage[];
	status: AgentChatStatus;
	error: string | null;
	/** Model messages for resuming after whiteboard lifecycle tool approval. */
	continuationMessages: ModelMessage[] | null;
	llmModel: string | null;
	messageListScroll: AgentChatMessageListScroll | null;
	/** Skill names loaded earlier in this tab session. */
	loadedSkillNames: string[];
};

let session: AgentChatSession = createEmptySession();

function createEmptySession(): AgentChatSession {
	const init =
		typeof window !== "undefined"
			? (
					window as unknown as {
						__INITIAL_AGENT_CHAT_SESSION__?: Partial<AgentChatSession>;
					}
				).__INITIAL_AGENT_CHAT_SESSION__
			: undefined;

	return {
		chatId: init?.chatId ?? null,
		messages: init?.messages ?? [],
		status: "idle",
		error: null,
		continuationMessages: null,
		llmModel: null,
		messageListScroll: null,
		loadedSkillNames: [],
	};
}

function normalizePersistedStatus(
	status: AgentChatStatus,
	messages: AgentChatMessage[],
): AgentChatStatus {
	// Submitting cannot survive reload / remount — there is no live stream.
	if (status === "submitting") {
		return "idle";
	}

	if (hasPendingApprovals(messages)) {
		return "awaiting_approval";
	}

	if (status === "awaiting_approval") {
		return "idle";
	}

	return status;
}

export function getAgentChatSession(): AgentChatSession {
	const messages = [...session.messages];
	return {
		chatId: session.chatId,
		messages,
		status: normalizePersistedStatus(session.status, messages),
		error: session.error,
		continuationMessages: session.continuationMessages
			? [...session.continuationMessages]
			: null,
		llmModel: session.llmModel,
		messageListScroll: session.messageListScroll
			? { ...session.messageListScroll }
			: null,
		loadedSkillNames: [...session.loadedSkillNames],
	};
}

export function setAgentChatSession(partial: Partial<AgentChatSession>) {
	const messages = partial.messages ?? session.messages;
	const nextStatus = normalizePersistedStatus(
		partial.status ?? session.status,
		messages,
	);

	session = {
		chatId: partial.chatId !== undefined ? partial.chatId : session.chatId,
		messages,
		status: nextStatus,
		error: partial.error !== undefined ? partial.error : session.error,
		continuationMessages:
			partial.continuationMessages !== undefined
				? partial.continuationMessages
				: session.continuationMessages,
		llmModel:
			partial.llmModel !== undefined ? partial.llmModel : session.llmModel,
		messageListScroll:
			partial.messageListScroll !== undefined
				? partial.messageListScroll
				: session.messageListScroll,
		loadedSkillNames:
			partial.loadedSkillNames !== undefined
				? [...partial.loadedSkillNames]
				: session.loadedSkillNames,
	};
}

export function clearAgentChatSession() {
	const { llmModel } = session;
	session = {
		...createEmptySession(),
		llmModel,
	};
}

export function resetAgentChatSessionsForTests() {
	session = createEmptySession();
}
